import { commitOptions } from "./commitSeams";
import { api } from "@/lib/api/client";

export type InboxChannel = "email" | "chat";
export interface InboxConnection {
  /** Optional server-owned consent destination; no provider URL is constructed by this client. */
  consent_url?: string | null;
  mode: "unconfigured" | "preview";
  message?: string;
  enabled: boolean;
  configured: boolean;
  eligible: boolean;
  status:
    | "configuration_pending"
    | "disabled"
    | "unavailable"
    | "managed_account_required"
    | "not_connected"
    | "active"
    | "reconnect_required"
    | "disconnect_pending"
    | "revoked";
  account_email: string | null;
  connection_id: string | null;
}
export type GmailConnection = InboxConnection;
export interface InboxPerson {
  user_id: string;
  name: string;
  role: "instructor" | "student";
  availability?: string;
}
export interface InboxContext {
  enabled: boolean;
  connection: GmailConnection;
  channels: Record<InboxChannel, InboxConnection>;
  course_id: string | null;
  courses: Array<{ id: string; title: string; role: "instructor" | "student" }>;
  recipients: InboxPerson[];
  sessions: Array<{ id: string; title: string; starts_at: string }>;
}
export interface InboxMessage {
  id: string;
  sender_user_id: string;
  body: string;
  subject: string;
  sent_at: string;
  direction: "sent" | "received";
  provider_message_id: string;
}
export interface InboxOperation {
  id: string;
  status: "pending" | "confirmed" | "known_failed" | "unknown";
  error_code: string | null;
  reconciled_at: string | null;
}
export interface CourseConversation {
  id: string;
  channel: InboxChannel;
  course_id: string;
  course_title: string;
  session_id: string | null;
  subject: string;
  participants: InboxPerson[];
  messages: InboxMessage[];
  latest_message_at: string | null;
  unread: boolean;
  refresh_state: "ready" | "stale" | "reconnect_required" | "unavailable";
  operations: InboxOperation[];
}
export interface InboxWriteResult {
  conversation: CourseConversation | null;
  operation: InboxOperation;
  commit?: InboxSendProjection;
}
export interface InboxSendProjection {
  state: "confirmed" | "refused" | "unknown";
  operation_id: string;
  conversation_id: string;
  course_id: string;
  reason: import("./commitSeams").CommitReason | null;
}
export interface NewConversation {
  channel: InboxChannel;
  course_id: string;
  session_id?: string | null;
  recipient_user_id: string;
  subject: string;
  body: string;
  operation_id: string;
}
function courseQuery(courseId?: string) {
  return courseId ? `?course_id=${encodeURIComponent(courseId)}` : "";
}
export interface InboxRanking {
  state:"ready"|"empty"|"unavailable";
  value?:{candidates:Array<{id:string;rank:number|null;eligible:boolean;blocked:boolean}>;lead:string|null};
}
export const inboxApi = {
  ranking:(course?:string)=>api.get<InboxRanking>(`/inbox/ranking${courseQuery(course)}`),
  connection: () => api.get<GmailConnection>("/gmail/connection"),
  connect: (returnTo: string) =>
    api.post<GmailConnection>("/gmail/connect", { return_to: returnTo }),
  disconnect: () => api.post<GmailConnection>("/gmail/disconnect"),
  thread: (courseId:string,recipient:string) => api.get<{person:{user_id:string;name:string};channel_modes:Record<InboxChannel,InboxConnection["mode"]>;conversations:CourseConversation[]}>(`/inbox/thread?${new URLSearchParams({course_id:courseId,recipient_user_id:recipient})}`),
  context: (courseId?: string) =>
    api.get<InboxContext>(`/inbox/context${courseQuery(courseId)}`),
  list: (courseId?: string, selectedId?: string) =>
    api.get<{ conversations: CourseConversation[] }>(
      `/inbox/conversations${selectedId ? `?${new URLSearchParams({...courseId ? {course_id:courseId} : {},conversation_id:selectedId})}` : courseQuery(courseId)}`,
    ),
  refreshAll: (courseId?: string) =>
    api.post<{ conversations: CourseConversation[] }>(
      `/inbox/refresh${courseQuery(courseId)}`,
    ),
  create: (request: NewConversation) =>
    api.post<InboxWriteResult>("/inbox/conversations", request),
  createKeyed: (request: NewConversation, key: string) =>
    api.post<InboxWriteResult>("/inbox/conversations", request, commitOptions(key)),
  replyKeyed: (id: string, body: string, key: string) =>
    api.post<InboxWriteResult>(`/inbox/conversations/${encodeURIComponent(id)}/messages`,
      {body, operation_id:key}, commitOptions(key)),
  operation: (id: string) => api.get<InboxWriteResult>(`/inbox/operations/${encodeURIComponent(id)}`, {cache:"no-store"}),
  get: (id: string) =>
    api.get<CourseConversation>(
      `/inbox/conversations/${encodeURIComponent(id)}`,
    ),
  reply: (id: string, body: string, operationId: string) =>
    api.post<InboxWriteResult>(
      `/inbox/conversations/${encodeURIComponent(id)}/messages`,
      { body, operation_id: operationId },
    ),
  refresh: (id: string) =>
    api.post<CourseConversation>(
      `/inbox/conversations/${encodeURIComponent(id)}/refresh`,
    ),
  read: (id: string) =>
    api.post<{ ok: boolean }>(
      `/inbox/conversations/${encodeURIComponent(id)}/read`,
    ),
  reconcile: (id: string) =>
    api.post<InboxWriteResult>(
      `/inbox/operations/${encodeURIComponent(id)}/reconcile`,
    ),
};
